import { Home as HomeIcon } from '@mui/icons-material'
import { alpha, Badge, Box, styled, Tabs, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router'
import { IMPERSONATION_BANNER_HEIGHT } from '@/components/ImpersonationBanner/ImpersonationBanner'
import { TabLink } from '@/helpers/compositions/TabLink'
import { T } from '@/helpers/T'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useChromeCollapsed } from '@/hooks/useChromeCollapse'
import { useRoles } from '@/hooks/useRoles'
import { useViewDomRect } from '@/hooks/useViewDomRect'
import type { TMessageID } from '@/locales/types'
import { useAuthorityUnreadCount } from '@/modules/Documentation/Authority/authority.api'
import { useAuthContext, useViewContext } from '@/providers/Global'
import { hasBaseRole } from '@/utils/authorities'

//	///////////////////////
//	MARK: CONFIG
const PATHS: {
  [key: string]: {
    lang: TMessageID
    code?: string
  }
} = {
  '/entities': { lang: 'lang.entities', code: 'role--entities' },
  '/accounting': { lang: 'lang.accounting', code: 'role--accounting' },
  '/human-resources': { lang: 'lang.human_resources', code: 'role--human-resources' },
  '/procurement': { lang: 'lang.procurement', code: 'role--procurement' },
  '/members': { lang: 'lang.members', code: 'role--members' },
  '/communications': { lang: 'lang.communications', code: 'role--communications' },
  '/operations': { lang: 'lang.operations', code: 'role--operations' },
  '/compliance': { lang: 'lang.compliance', code: 'role--compliance' },
  '/safety': { lang: 'lang.safety', code: 'role--safety' },
  '/maintenance': { lang: 'lang.maintenance', code: 'role--maintenance' },
  '/training': { lang: 'lang.training', code: 'role--training' },
  '/analytics': { lang: 'lang.analytics', code: 'role--analytics' },
  '/settings': { lang: 'lang.settings', code: 'role--settings' },
  '/tools': { lang: 'lang.tools', code: 'role--tools' },
  // Documentation : visible par tout utilisateur connecté (membres inclus) ; le contenu est filtré par ACL côté serveur.
  '/documentation': { lang: 'lang.documentation' },
}

//	///////////////////////
//	MARK: TYPES
export interface ICSubNavbarProps {}

//	///////////////////////
//	MARK: STYLED COMPONENT
const StyledTabLink = styled(TabLink)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.8125rem',
  textTransform: 'none',
  letterSpacing: 0.2,
  minHeight: 44,
  minWidth: 0,
  padding: theme.spacing(1, 2),
  color: theme.palette.text.secondary,
  transition: 'color 150ms ease, background-color 150ms ease',
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.common.black, 0.04),
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}))

//	///////////////////////
//	MARK: EXPORTS
const SubNavbarInternal: React.FC<ICSubNavbarProps> = (props) => {
  const theme = useTheme()
  const location = useLocation()
  const View = useViewContext()
  const Auth = useAuthContext()
  const { menuItems } = useRoles()
  // Pastille Boîte Autorité — mêmes rôles que l'API (le menuItem en est le miroir exact).
  const authorityUnread = useAuthorityUnreadCount(
    menuItems.has('role--compliance--authority-mailbox'),
  )
  const authorityBadge = authorityUnread.data?.data.count ?? 0

  const [isDesktop] = useBreakpoint('md')
  const [ref] = useViewDomRect('subNavbar')

  const chromeCollapsed = useChromeCollapsed()

  const bannerOffset = Auth.active?.impersonation ? IMPERSONATION_BANNER_HEIGHT : 0

  const [pathKeys, pathEntries] = useMemo(() => {
    const v = { ...PATHS }

    for (const key of Object.keys(v)) {
      const { code } = v[key]

      if (code && !menuItems.has(code)) {
        delete v[key]
      }
    }

    // Rôles non étendus par la hiérarchie : voir BASE_ROLE_HOLDERS.
    const roles = Auth.active?.roles ?? []

    if (!hasBaseRole(roles)) {
      delete v['/documentation']
    }

    return [Object.keys(v), Object.entries(v)]
  }, [menuItems, Auth.active?.roles])

  const value = useMemo<string | false>(() => {
    if (location.pathname === '/' || location.pathname.startsWith('/dashboard')) {
      return '/'
    }

    for (const key of pathKeys) {
      if (location.pathname.startsWith(key)) {
        return key
      }
    }

    return false
  }, [location.pathname, pathKeys])

  return (
    <Box
      ref={ref}
      display='flex'
      visibility={chromeCollapsed ? 'hidden' : undefined}
      position='fixed'
      width='100%'
      top={`${bannerOffset + View.navbar.top + View.navbar.height}px`}
      bgcolor='grey.300'
      zIndex={theme.zIndex.appBar}
      pr={isDesktop ? `${View.quickActionsAdd.width + 36}px` : undefined}
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
        backgroundImage: `linear-gradient(180deg, ${alpha(
          theme.palette.common.white,
          0.6,
        )} 0%, transparent 100%)`,
      }}
    >
      <Tabs
        value={value}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        sx={{
          minHeight: 44,
          '& .MuiTabs-indicator': {
            height: 3,
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            backgroundColor: theme.palette.primary.main,
          },
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            opacity: 0.3,
          },
        }}
      >
        <StyledTabLink
          value='/'
          to='/'
          label={
            <Tooltip title={<T id='lang.my_dashboard' />}>
              <HomeIcon fontSize='small' />
            </Tooltip>
          }
        />
        {pathEntries.map(([link, data]) => {
          const badgeContent = link === '/compliance' ? authorityBadge : 0

          return (
            <StyledTabLink
              value={link}
              key={data.lang}
              to={link}
              label={
                badgeContent > 0 ? (
                  <Badge
                    color='error'
                    badgeContent={badgeContent}
                    sx={{
                      alignItems: 'center',
                      gap: 0.75,
                      // Le scroller des Tabs coupe tout débordement : le badge doit rester dans la boîte de l'onglet.
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                      },
                    }}
                  >
                    <T id={data.lang} />
                  </Badge>
                ) : (
                  <T id={data.lang} />
                )
              }
            />
          )
        })}
      </Tabs>
    </Box>
  )
}

export const SubNavbar = React.memo(SubNavbarInternal) as typeof SubNavbarInternal
